/** @jsxImportSource @emotion/react */
import { SortDirection } from '../general/models/sort-direction'
import {
    useLazyGetOrdersQuery,
    useForceRegenerateOrderMutation,
    useRegenerateOrderMutation,
    useRegenerateOrderAndCallbackMutation,
} from './orders.service'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../redux/hooks'
import { selectOrdersFilters } from './order.slice'
import { usePrevious } from '../general/use-previous'
import { Button, IconButton, Paper } from '@mui/material'
import { KotiTable } from '../general/table/koti-table'
import { OrderTableItem } from './models/order'
import { Loader } from '../general/loader/loader'
import { Config } from '../../redux/config'
import { TFunction } from 'i18next'
import { TableStructure } from '../general/table/table-structure.type'
import { formatDateTime, getDifferenceFromNow } from '../../utils/date-utils'
import { Download, NotificationsActive, OpenInNew, Sync } from '@mui/icons-material'
import { getProductList } from './orders-helper'
import { MultiStringViewer } from '../general/multi-viewer/multi-string-viewer'
import TablePagination from '@mui/material/TablePagination'
import { OrdersTableFilters } from './OrdersTableFilters'
import { isOsloCadastre } from 'utils/cadastre-utils'
import { PropertyDelayedFeesDialog } from './property-delayed-fees-dialog'
import { useAuthContextRole } from '../auth/auth.slice'
import { Role } from '../auth/role'

const onDownload = (object: OrderTableItem) => {
    const link = document.createElement('a')
    link.href = `${Config.backendUrl}/v1/orders/${object.orderId}/pdf/${object.index}?accessToken=${object.pdf.accessToken}`
    link.click()
}

const getOrdersTableStructure = (
    t: TFunction,
    onSubmit: any,
    regen: any,
    regenAndCallback: any,
    forceRegen: any,
    openInterestRateDialog: (id: string) => void,
    isCubitAdmin: boolean,
): TableStructure<OrderTableItem>[] => [
    {
        label: t('TEXT_DATE', 'Dato'),
        columnName: 'created',
        sortFunction: (object) => object.created.getTime(),
        value: (object) => formatDateTime(object.created),
    },
    {
        label: t('TEXT_PROJECT_NR', 'Oppdragsnr.'),
        value: (object) => object.note,
    },
    {
        label: t('TEXT_ORDER_NR', 'Ordrenr.'),
        columnName: 'orderId',
        sortFunction: (object) => object.orderNumber,
        value: (object) => object.orderNumber,
    },
    {
        label: t('TEXT_PRODUCT_NAME', 'Produkt'),
        sortFunction: (object) => object.product.name,
        value: (object) => object.product.name,
    },
    {
        label: t('TEXT_ADDRESS', 'Adresse'),
        sortFunction: (object) => (object.address ? object.address.addressText : ''),
        value: (object) =>
            object.address ? <MultiStringViewer items={object.address.addressText.split(',')} owners={false} /> : '',
    },
    {
        label: t('TEXT_POStAL_CODE', 'Postnummer'),
        sortFunction: (object) => object.address.postalCode,
        value: (object) => object.address && `${object.address.postalCode || ''} ${object.address.postalName || ''}`,
    },
    {
        label: t('TEXT_CADASTRE_NR', 'Matrikkelnr.'),
        sortFunction: (object) => object.cadastreNr,
        value: (object) => object.cadastreNr,
    },
    {
        label: t('TEXT_STATUS'),
        sortFunction: (object) => object.status,
        // t('ORDER_ITEM_STATUS_Pending', 'Pending')
        // t('ORDER_ITEM_STATUS_Processing', 'Processing')
        // t('ORDER_ITEM_STATUS_Completed', 'Completed')
        // t('ORDER_ITEM_STATUS_Rejected', 'Rejected')
        // t('ORDER_ITEM_STATUS_Failed', 'Failed')
        value: (object) => t(`ORDER_ITEM_STATUS_${object.status}`),
    },
    {
        label: t('TEXT_DOWNLOAD', 'Last ned'),
        value: (object) => {
            switch (object.status) {
                case 'Pending':
                case 'Rejected':
                    return null

                case 'Completed': {
                    return (
                        <IconButton
                            aria-label={`${t('TEXT_DOWNLOAD', 'Last ned')}`}
                            name={'download'}
                            onClick={(e) => {
                                onDownload(object)
                                e.stopPropagation()
                            }}
                        >
                            <Download aria-label={t('TEXT_DOWNLOAD', 'Last ned')} />
                        </IconButton>
                    )
                }

                case 'Processing': {
                    let minutesDiff = 0
                    if (object.created) {
                        minutesDiff = getDifferenceFromNow(object.generated ?? object.created, 'minutes')
                    }

                    if (Math.abs(minutesDiff) > 1) {
                        return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    sx={{ borderColor: '#000', minWidth: 'auto', marginLeft: '10px' }}
                                    size={'small'}
                                    variant={'outlined'}
                                    onClick={(e) => {
                                        onSubmit()
                                        e.stopPropagation()
                                    }}
                                >
                                    <Sync fontSize={'small'} />
                                </Button>
                            </div>
                        )
                    }

                    return null
                }

                case 'Failed': {
                    // Regeneration is a Cubit-admin-only operation (enforced server-side); hide it otherwise.
                    if (!isCubitAdmin) return null

                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                sx={{ borderColor: '#000', minWidth: 'auto' }}
                                size={'small'}
                                variant={'outlined'}
                                title={t('TEXT_REGENERATE', 'Generer på nytt')}
                                onClick={(e) => {
                                    regen(object.orderId)
                                    e.stopPropagation()
                                }}
                            >
                                <Sync fontSize={'small'} />
                            </Button>
                            <Button
                                sx={{ borderColor: '#000', minWidth: 'auto', marginLeft: '10px' }}
                                size={'small'}
                                variant={'outlined'}
                                title={t('TEXT_REGENERATE_AND_NOTIFY', 'Generer på nytt og varsle kunde')}
                                onClick={(e) => {
                                    regenAndCallback(object.orderId)
                                    e.stopPropagation()
                                }}
                            >
                                <NotificationsActive fontSize={'small'} />
                            </Button>
                        </div>
                    )
                }

                default:
                    return null
            }
        },
    },
    {
        label: t('TEXT_INTEREST_RATES_COLUMN_NAME', 'Forsrenter'),
        value: (object) => {
            if (!object.pdf || !isOsloCadastre(object.cadastreNr) || object.product.type !== 'Taxes') return null
            return (
                <IconButton
                    aria-label={t('TEXT_OPEN_INTEREST_RATES_DIALOG', 'Open interest rates dialog')}
                    onClick={(e) => {
                        e.stopPropagation()
                        openInterestRateDialog(object.uniqueId)
                    }}
                >
                    <OpenInNew aria-label={t('TEXT_OPEN_INTEREST_RATES_DIALOG', 'Open interest rates dialog')} />
                </IconButton>
            )
        },
    },
]

export const MyOrders: React.FC<{ reload: any }> = ({ reload }) => {
    const { t } = useTranslation()
    const isCubitAdmin = useAuthContextRole() === Role.CubitAdmin
    const filters = useAppSelector(selectOrdersFilters)
    const prevFilters = usePrevious(filters)

    const [regenerate] = useRegenerateOrderMutation()
    const [forceRegenerate] = useForceRegenerateOrderMutation()
    const [regenerateAndCallback] = useRegenerateOrderAndCallbackMutation()
    const [trigger, { data }] = useLazyGetOrdersQuery()

    const [pageNumber, setPageNumber] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [sortBy, setSortBy] = useState('created')
    const [direction, setDirection] = useState<SortDirection>('desc')
    const [selectedOrderForDelayedFees, setSelectedOrderForDelayedFees] = React.useState<string>()

    const withoutCacheSubmit = () =>
        trigger({ page: pageNumber, itemsPerPage: itemsPerPage, sortBy, direction, ...filters }, false)

    useEffect(() => {
        withoutCacheSubmit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload])

    useEffect(() => {
        if (prevFilters === filters) {
            trigger({ page: pageNumber, itemsPerPage: itemsPerPage, sortBy, direction, ...filters })
        }
    }, [pageNumber, itemsPerPage, direction, trigger, sortBy, filters, prevFilters])

    const handleOnSortChange = (orderBy: string, order: SortDirection) => {
        setSortBy(orderBy)
        setDirection(order)
    }

    const onSubmit = () => trigger({ page: pageNumber, itemsPerPage: itemsPerPage, sortBy, direction, ...filters })

    const regen = (orderId: string) => {
        regenerate({ id: orderId })
    }

    const regenAndCallback = (orderId: string) => {
        regenerateAndCallback({ id: orderId })
    }

    const forceRegen = (orderId: string) => {
        forceRegenerate({ id: orderId })
    }

    const handleOpenDelayedFeesDialog = (orderLineId: string) => setSelectedOrderForDelayedFees(orderLineId)

    if (!data || !data.data) return <Loader />

    const orderLines = getProductList(data.data).filter(
        (line) => !filters.itemStatus || line.status === filters.itemStatus,
    )

    return (
        <>
            <Paper elevation={0}>
                <OrdersTableFilters onSubmit={onSubmit} />
                <KotiTable
                    selectable={false}
                    pagination={false}
                    rowsPerPageOptions={[]}
                    tableStructure={getOrdersTableStructure(t, withoutCacheSubmit, regen, regenAndCallback, forceRegen, handleOpenDelayedFeesDialog, isCubitAdmin)}
                    rowsPerPage={orderLines.length}
                    page={pageNumber}
                    rows={orderLines}
                    total={orderLines.length}
                    onSortChanged={handleOnSortChange}
                    handleChangePage={setPageNumber}
                    handleChangeRowsPerPage={setItemsPerPage}
                />
                <TablePagination
                    labelRowsPerPage={<span>{t('ROWS_PER_PAGE', 'Rader per side')}</span>}
                    component="div"
                    count={data.total}
                    rowsPerPage={itemsPerPage}
                    page={pageNumber}
                    onPageChange={(event, newPage) => setPageNumber(newPage)}
                    onRowsPerPageChange={(event) => setItemsPerPage(parseInt(event.target.value))}
                />
            </Paper>

            <PropertyDelayedFeesDialog
                isOpen={Boolean(selectedOrderForDelayedFees)}
                onClose={() => setSelectedOrderForDelayedFees(undefined)}
                orderLine={
                    selectedOrderForDelayedFees
                        ? orderLines.find((o) => o.uniqueId === selectedOrderForDelayedFees)
                        : undefined
                }
            />
        </>
    )
}
